import React, { createRef, forwardRef, useEffect } from 'react';
import Datepicker from 'react-datepicker';
import { useField, useForm } from 'react-final-form';
import { styled } from '@linaria/react';
import { addHours, differenceInHours, differenceInMinutes, getHours, startOfToday } from 'date-fns';

import {
  DEADLINE_FORMAT_CLIENT_DATE_FNS,
  ERROR_DEADLINE_EMPTY,
  PAPER_SIZE_VALUES,
} from '../../constants';
import { FieldError, Hint, Input, Label, Warning, When } from '../../ui';
import { Calendar } from '../../ui/icon';
import { composeValidators, convertWordsToPages } from '../../utils';
import { required } from '../../utils/validators';

import 'react-datepicker/dist/react-datepicker.css';

const isRequired = required(ERROR_DEADLINE_EMPTY);

const DeadlineRow = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
`;

const DeadlineField = styled.div`
  position: relative;

  & input:hover {
    cursor: pointer;
  }

  .react-datepicker-popper {
    z-index: 2;
  }

  .react-datepicker {
    width: 320px;
    border: none;
    border-radius: 4px;
    box-shadow: 0px 8px 16px rgb(66 66 66 / 20%);
    display: flex;
    flex-wrap: wrap;
    background-color: #fff;
  }

  .react-datepicker__time-container,
  .react-datepicker__month-container {
    float: none;
    box-sizing: border-box;
  }

  .react-datepicker__time-container {
    border-color: #dde6ef;
  }

  .react-datepicker__month-container {
    max-width: 235px;
  }

  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker__header {
    background-color: var(--bb-datepicker-header-background);
    border-color: #dde6ef;
  }

  .react-datepicker__header--time {
    padding-bottom: 32px;
  }

  .react-datepicker__time-list-item {
    display: flex;
    align-items: center;
  }

  .react-datepicker__time-list-item::before,
  .react-datepicker__time-list-item::after {
    content: none;
  }

  .react-datepicker__day {
    outline: none;
  }

  .react-datepicker__day--selected,
  .react-datepicker__time-list-item--selected {
    background-color: var(--bb-datepicker--selected) !important;
  }

  .react-datepicker__day--outside-month {
    background-color: transparent !important;
    color: inherit;
  }

  .react-datepicker__day--keyboard-selected {
    box-shadow: 0 0 0 2px var(--bb-datepicker--selected);
    background-color: transparent;
    color: inherit;
  }

  .react-datepicker__time-box {
    width: auto !important;
  }

  .react-datepicker__day--disabled {
    color: #ccc;
  }
`;

const DeadlineConfirm = styled.button`
  background-color: var(--bb-datepicker-confirm-background);
  color: var(--bb-datepicker-confirm-color);
  width: 100%;
  border: none;
  padding-top: 12px;
  padding-bottom: 12px;
  border-radius: 0 0 4px 4px;
  font-size: var(--bb-datepicker-confirm-font-size);
  font-weight: var(--bb-datepicker-confirm-font-weight);

  &:hover {
    cursor: pointer;
    background-color: var(--bb-datepicker-confirm-background--hover);
  }
`;

const DeadlineCalendar = styled.label`
  position: absolute;
  right: 0;
  top: 50%;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 3px 3px 0;
  margin-top: -22.5px;
  pointer-events: none;
`;

/**
 * @param {Date} date
 */
const filterTime = date => differenceInHours(date, new Date()) > 1;

/**
 * @param {Date} date
 */
const getTimeLeft = date => {
  if (!date) return;

  const hours = differenceInHours(date, new Date());
  const minutes = differenceInMinutes(date, new Date());

  if (hours > 1) {
    const h = hours % 24;
    const d = (hours - h) / 24;
    const m = minutes % 60;
    const pattern = ['d', 'h', 'm'];

    const values = d > 0 ? [d, h] : [d, h, m];

    /**
     * @example '1d 12h left'
     * @example '1d left'
     * @example '13h 23m left'
     */
    return values.reduceRight((acc, c, i) => (c ? `${c}${pattern[i]} `.concat(acc) : acc), ' left');
  }
};

const withShortDeadline = (pages, { valid }, deadline_datetime) =>
  valid && pages > differenceInHours(new Date(deadline_datetime), new Date());

const getDeadlineRecommendation = pages =>
  addHours(startOfToday(), getHours(new Date()) + parseInt(pages, 10) + 1);

const deadlineRecommendation = pages =>
  differenceInHours(getDeadlineRecommendation(pages), new Date());

const Deadline = () => {
  const { input, meta } = useField('deadline_datetime', {
    validate: composeValidators(isRequired),
  });

  const { input: pagesInput } = useField('pages_qty', {
    subscription: { value: true },
  });
  const { input: wordsInput } = useField('words_qty', {
    subscription: { value: true },
  });

  const CustomInput = forwardRef((props, ref) => (
    <Input
      innerRef={ref}
      {...props}
      readOnly
      meta={meta}
      suppressHydrationWarning
    />
  ));
  const datepickerRef = createRef();

  const { mutators } = useForm();
  const timeLeft = getTimeLeft(new Date(input.value));

  /**
   * XXX document side effect
   */
  useEffect(() => {
    const list = document.querySelector('.react-datepicker__time-list');
    const selectedTimeNode = document.querySelector('.react-datepicker__time-list-item--selected');

    if (selectedTimeNode) {
      list.scroll(0, selectedTimeNode.offsetTop);
    }
  }, [input.value]);

  return (
    <>
      <Label>Deadline</Label>

      <DeadlineRow>
        <DeadlineField>
          <Datepicker
            ref={datepickerRef}
            showTimeSelect
            timeIntervals={60}
            customInput={<CustomInput />}
            onSelect={date => mutators.correctTime(date)}
            dateFormat={DEADLINE_FORMAT_CLIENT_DATE_FNS}
            selected={input.value}
            minDate={new Date()}
            filterTime={filterTime}
            placeholderText="Select date"
            name="deadline_datetime"
            {...input}
            suppressHydrationWarning
          >
            <DeadlineConfirm
              type="button"
              onClick={() => datepickerRef.current.setOpen(false)}
            >
              Confirm
            </DeadlineConfirm>
          </Datepicker>

          <DeadlineCalendar>
            <Calendar />
          </DeadlineCalendar>
        </DeadlineField>

        <Hint>{timeLeft}</Hint>
      </DeadlineRow>

      <FieldError meta={meta} />

      <When
        field="paper-size-type"
        is={PAPER_SIZE_VALUES.PAGES}
      >
        <When
          field="pages_qty"
          is={(...args) => withShortDeadline(...args, input.value)}
          subscription={{ value: true, valid: true }}
        >
          <Warning
            onClick={() =>
              mutators.setRecommendedDeadline(getDeadlineRecommendation(pagesInput.value))
            }
          >
            We recommend leaving at least{' '}
            <b suppressHydrationWarning>{deadlineRecommendation(pagesInput.value)}</b> hours to
            complete the order well
          </Warning>
        </When>
      </When>

      <When
        field="paper-size-type"
        is={PAPER_SIZE_VALUES.WORDS}
      >
        <When
          field="words_qty"
          is={(...args) => {
            const [words, state] = args;
            return withShortDeadline(convertWordsToPages(words), state, input.value);
          }}
          subscription={{ value: true, valid: true }}
        >
          <Warning
            onClick={() =>
              mutators.setRecommendedDeadline(
                getDeadlineRecommendation(convertWordsToPages(wordsInput.value)),
              )
            }
          >
            We recommend leaving at least{' '}
            <b suppressHydrationWarning>
              {deadlineRecommendation(convertWordsToPages(wordsInput.value))}
            </b>{' '}
            hours to complete the order well
          </Warning>
        </When>
      </When>
    </>
  );
};

export default Deadline;
